/*
 * Delta Don — product card FX (second-image crossfade).
 *
 * Horizon's product-card switches to the next slideshow slide instantly on
 * pointerenter (assets/product-card.js → previewImage, animate:false), which
 * reads as a hard cut and can white-flash on a cold cache. The slideshow
 * already dispatches a bubbling `slideshow:select` event with
 * { index, previousIndex, userInitiated } — this script turns that instant
 * swap into a crossfade purely from the outside:
 *
 *   on slideshow:select inside a product-card (non-user-initiated):
 *     1. clone the outgoing slide's <img> into a .dd-card-fade ghost overlay
 *     2. prepend it to .card-gallery (below badges/quick-add in paint order)
 *     3. fade it out on the next frame, then remove it
 *
 * No theme JS is modified; if this file fails to load, cards behave exactly
 * as before (instant swap). Guards mirror the theme's own previewImage gates:
 * hover-capable fine pointers only, reduced-motion users opted out.
 *
 * Styles for the ghost live in assets/dd-cards-fx.css.
 */
(() => {
  const hoverFine = window.matchMedia('(hover: hover) and (pointer: fine)');
  const motionOK = window.matchMedia('(prefers-reduced-motion: no-preference)');

  let listening = false;

  const FADE_MS = 320; // keep in sync with .dd-card-fade--out in dd-cards-fx.css

  function onSlideSelect(event) {
    const detail = event.detail;
    if (!detail || detail.userInitiated) return;
    if (detail.previousIndex == null || detail.previousIndex === detail.index) return;

    const slideshow = event.target;
    const card = slideshow.closest?.('product-card');
    if (!card) return;

    const gallery = slideshow.closest('.card-gallery');
    if (!gallery) return;

    // Use the component's own slide list (its `slides` getter filters out
    // [hidden] variant slides), so previousIndex resolves exactly the way the
    // slideshow resolved it. Fall back to a raw DOM query for safety.
    const slides =
      Array.isArray(slideshow.slides) && slideshow.slides.length
        ? slideshow.slides
        : Array.from(slideshow.querySelectorAll('slideshow-slide'));
    const prevSlide = slides[detail.previousIndex];
    const prevImg = prevSlide?.querySelector('img');
    if (!prevImg) return;

    // One ghost at a time — a rapid leave/enter replaces the in-flight fade.
    gallery.querySelector('.dd-card-fade')?.remove();

    const ghost = prevImg.cloneNode(false);
    // Pin the exact URL the browser already resolved so the ghost never
    // re-evaluates srcset (no refetch, no flash of a different candidate).
    ghost.removeAttribute('srcset');
    ghost.removeAttribute('sizes');
    ghost.removeAttribute('loading');
    ghost.src = prevImg.currentSrc || prevImg.src;
    ghost.alt = '';
    ghost.setAttribute('aria-hidden', 'true');

    const overlay = document.createElement('div');
    overlay.className = 'dd-card-fade';
    overlay.appendChild(ghost);
    gallery.prepend(overlay);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        overlay.classList.add('dd-card-fade--out');
      });
    });

    const cleanup = () => overlay.remove();
    overlay.addEventListener('transitionend', cleanup, { once: true });
    setTimeout(cleanup, FADE_MS + 250); // safety net if transitionend is lost
  }

  function sync() {
    const shouldListen = hoverFine.matches && motionOK.matches;
    if (shouldListen && !listening) {
      document.addEventListener('slideshow:select', onSlideSelect);
      listening = true;
    } else if (!shouldListen && listening) {
      document.removeEventListener('slideshow:select', onSlideSelect);
      document.querySelectorAll('.dd-card-fade').forEach((el) => el.remove());
      listening = false;
    }
  }

  hoverFine.addEventListener?.('change', sync);
  motionOK.addEventListener?.('change', sync);
  sync();
})();
